<script setup lang="ts">
import TaskCard from '@/components/TaskCard.vue';
import { type LumarActivityWeekDto, useAppApi } from '@/plugins/appApi';
import { useProjectStore } from '@/stores/project';
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import moment from 'moment';
import useLoading from '@/composables/useLoading';
import withRetry from '@/composables/withRetry';
import { useNetworkStore } from '@/stores/network';

const router = useRouter();
const projectStore = useProjectStore();
const loading = ref(false);

const oneWeekAgo = new Date();
oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

const allActivities = ref<LumarActivityWeekDto[]>([]);

const currentActivities = computed<LumarActivityWeekDto[]>(() => {
  return allActivities.value
    .filter((a) => {
      return (
        moment(a.year.toString() + 'W' + a.week.toString())
          .toDate()
          .getTime() > oneWeekAgo.getTime()
      );
    })
    .sort((a, b) => {
      if (a.year > b.year) {
        return a.week > b.week ? -1 : 1;
      }

      return b.week > a.week ? -1 : 1;
    });
});
const pastActivities = computed<LumarActivityWeekDto[]>(() => {
  return allActivities.value
    .filter((a) => {
      return (
        moment(a.year.toString() + 'W' + a.week.toString())
          .toDate()
          .getTime() < oneWeekAgo.getTime()
      );
    })
    .sort((a, b) => {
      if (a.year > b.year) {
        return a.week > b.week ? -1 : 1;
      }

      return b.week > a.week ? -1 : 1;
    });
});

const loadActivities = async () => {
  const client = useAppApi();

  try {
    const { data, response } = await withRetry(() =>
      client.GET('/api/projects/{uid}/activities', {
        params: {
          path: {
            uid: projectStore.getProjectUid()
          }
        }
      })
    );

    if (!data) {
      // TODO: error handling...
      throw response.status;
    }

    allActivities.value = data;
  } catch (e) {
    useNetworkStore().signalIssue(e);
  }
};

onMounted(() => useLoading(loading, loadActivities));
</script>

<template>
  <div class="flex flex-col pb-12">
    <div class="flex items-center gap-2 mb-6">
      <Button
        icon="pi pi-angle-left"
        size="small"
        rounded
        aria-label="arrow right"
        @click="router.back()"
      />
    </div>
    <h1 class="text-2xl min-[1300px]:text-5xl text-lumar-blue font-bold mb-8">
      {{ $t('lumarActivities.activities') }}
    </h1>
    <!-- <IconField class="mb-4">
      <InputIcon class="pi pi-search" />
      <InputText v-model="search" class="w-full" placeholder="Vpišite ključno besedo" />
    </IconField>
    <div class="mb-10">filters</div> -->
    <div class="flex flex-col gap-5 mb-10">
      <h2 class="text-xl min-[1300px]:text-4xl text-lumar-blue">
        {{ $t('lumarActivities.currentActivities') }}
      </h2>
      <div
        class="flex flex-col gap-1 min-[1300px]:grid min-[1300px]:grid-cols-2 min-[1300px]:gap-4"
      >
        <Skeleton
          height="104px"
          v-if="loading"
          :pt="{
            root: {
              style: {
                background: 'var(--color-lumar-light-gray)'
              }
            }
          }"
        ></Skeleton>
        <Skeleton
          height="104px"
          v-if="loading"
          :pt="{
            root: {
              style: {
                background: 'var(--color-lumar-light-gray)'
              }
            }
          }"
        ></Skeleton>
        <Skeleton
          height="104px"
          v-if="loading"
          :pt="{
            root: {
              style: {
                background: 'var(--color-lumar-light-gray)'
              }
            }
          }"
        ></Skeleton>
        <div v-if="loading">{{ $t('lumarActivities.loadingActivities') }}</div>
        <div v-if="!currentActivities.length && !loading">
          {{ $t('lumarActivities.noCurrentActivities') }}
        </div>
        <div v-for="activity in currentActivities" :key="activity.year + activity.week">
          <TaskCard :activity />
        </div>
      </div>
    </div>
    <div class="flex flex-col gap-5" v-if="pastActivities.length">
      <h2 class="text-xl min-[1300px]:text-4xl text-lumar-blue">
        {{ $t('lumarActivities.pastActivities') }}
      </h2>
      <DataView :value="pastActivities">
        <template #list="slotProps">
          <div
            class="flex flex-col gap-1 min-[1300px]:grid min-[1300px]:grid-cols-2 min-[1300px]:gap-4"
          >
            <div v-for="activity in slotProps.items" :key="activity.year + activity.week">
              <TaskCard :activity />
            </div>
          </div>
        </template>
      </DataView>
    </div>
  </div>
</template>
